import {
  Box,
  TextField,
  Typography,
  Link,
  Alert,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname } from "../../../utils";

interface GoHighLevelIntegrationFormProps {
  editMode?: boolean;
  initialData?: {
    accessToken?: string;
    locationId?: string;
  };
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: { accessToken: string; locationId?: string }) => void;
  getToken: () => Promise<string>;
}

export default function GoHighLevelIntegrationForm({
  editMode = false,
  initialData,
  onValidationChange,
  onDataChange,
  getToken,
}: GoHighLevelIntegrationFormProps) {
  const [accessToken, setAccessToken] = useState(
    initialData?.accessToken || ""
  );
  const [locationId, setLocationId] = useState(initialData?.locationId || "");
  const [testingCredentials, setTestingCredentials] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    locationId?: string;
  } | null>(null);

  // Validate form
  useEffect(() => {
    const isValid = accessToken.trim() !== "";
    onValidationChange(isValid);
  }, [accessToken, onValidationChange]);

  // Notify parent of data changes (trimmed values)
  useEffect(() => {
    onDataChange({
      accessToken: accessToken.trim(),
      locationId: locationId.trim() || undefined,
    });
  }, [accessToken, locationId, onDataChange]);

  // Clear test result when credentials change
  useEffect(() => {
    setTestResult(null);
  }, [accessToken, locationId]);

  const handleTestCredentials = async () => {
    setTestingCredentials(true);
    setTestResult(null);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/crm/test-credentials/gohighlevel`,
        {
          accessToken: accessToken.trim(),
          locationId: locationId.trim() || undefined,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setTestResult({
          success: true,
          message: response.data.message || "Connection successful!",
          locationId: response.data.locationId,
        });
        // Auto-fill location ID if it was extracted from the token
        if (response.data.locationId && !locationId.trim()) {
          setLocationId(response.data.locationId);
        }
      } else {
        setTestResult({
          success: false,
          message: response.data.error || "Connection failed",
        });
      }
    } catch (error: any) {
      setTestResult({
        success: false,
        message:
          error.response?.data?.error ||
          error.message ||
          "Failed to test credentials",
      });
    } finally {
      setTestingCredentials(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Connect your GoHighLevel account to sync contacts and log activities
        when leads reply to your sequences.
      </Typography>

      <TextField
        sx={{ mt: 1 }}
        fullWidth
        type="password"
        value={accessToken}
        label="GoHighLevel API Key"
        helperText={
          <>
            Find your API key in GoHighLevel Settings → Integrations → Private
            Integrations.{" "}
            <Link
              href="https://help.gohighlevel.com/support/solutions/articles/155000003054-private-integrations-everything-you-need-to-know"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more
            </Link>
          </>
        }
        onChange={(e) => setAccessToken(e.target.value)}
        required
      />

      <TextField
        sx={{ mt: 2 }}
        fullWidth
        value={locationId}
        label="Location ID (Optional)"
        helperText={
          <>
            Required for Private Integration Tokens (PIT). Find it in
            GoHighLevel Settings → Business Info. For JWT tokens, this is
            extracted automatically.
          </>
        }
        onChange={(e) => setLocationId(e.target.value)}
      />

      <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <LoadingButton
          variant="outlined"
          onClick={handleTestCredentials}
          loading={testingCredentials}
          disabled={!accessToken.trim()}
        >
          Test Credentials
        </LoadingButton>

        {testingCredentials && (
          <Typography variant="body2" color="text.secondary">
            Testing connection...
          </Typography>
        )}
      </Box>

      {testResult && (
        <Alert
          severity={testResult.success ? "success" : "error"}
          sx={{ mt: 2 }}
        >
          {testResult.message}
          {testResult.success && testResult.locationId && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Location ID: {testResult.locationId}
            </Typography>
          )}
        </Alert>
      )}
    </Box>
  );
}
